// ── METER.M1 (#2993) — closed vocabulary for execution_metrics.agent_type ────
//
// Every model call BluePrint records is labelled with exactly one of these
// values. Before this module the five recordMetrics call sites wrote free
// text — the lowercase registry key ('pm_requirements'), the literal
// 'unknown', or 'dev_agent' for work that was not Phase 4 — so a
// GROUP BY agent_type split one phase across several labels.
//
// Phase labels are the eight PhaseAgentType values; the phase number is always
// derived from PHASE_AGENTS (phaseForLabel), never stored. Support labels name
// the non-phase work found by the Sprint METER census
// (Sprint-Artefacts/Sprint-METER-recon.md §2). Anything unrecognised is OTHER.

import { PHASE_AGENTS, type PhaseAgentType } from './phase-agents.ts';

export const SUPPORT_METER_LABELS = [
  'SESSION_SUMMARY',
  'DEV_REFLECTION',
  'PROMPT_EXTRACTION',
  'ARTEFACT_CLASSIFICATION',
  'QUALITY_RUBRIC',
  'RISK_DISPOSITION',
  'NFR_DISPOSITION',
  'OPEN_QUESTION_PROPOSAL',
  'INCORPORATION_JUDGE',
  'MEETING_EXTRACTION',
  'SPIKE_CHANGE_ORDER',
  'CR_AUTO_RESOLVE',
  'BROWNFIELD_ASSESSMENT',
  'CODEBASE_SUMMARY',
  'SPRINT_CONTRACT',
  'MA_COORDINATOR',
  'EXECUTION_AGENT',
  'CHANGE_ORDER',
  'HELP',
  'OTHER',
] as const;

export type SupportMeterLabel = (typeof SUPPORT_METER_LABELS)[number];
export type MeterLabel = PhaseAgentType | SupportMeterLabel;

export const METER_LABELS: readonly MeterLabel[] = [
  ...PHASE_AGENTS.map((a) => a.agentType),
  ...SUPPORT_METER_LABELS,
];

const METER_LABEL_SET: ReadonlySet<string> = new Set(METER_LABELS);

export function isMeterLabel(value: unknown): value is MeterLabel {
  return typeof value === 'string' && METER_LABEL_SET.has(value);
}

/**
 * Registry key (agent_sessions.agent_name, agentRegistry.ts) → PhaseAgentType.
 * Single source of truth, shared with the model resolver in
 * apps/api/src/services/agentModelConfig.ts. Must cover all eight phase agents
 * (enforced by meter-labels.test.ts) — `ux_design` was missing from the
 * resolver's private copy before #2993.
 */
export const REGISTRY_KEY_TO_PHASE_AGENT: Readonly<Record<string, PhaseAgentType>> = {
  pm_requirements: 'PM_AGENT',
  architecture:    'ARCHITECTURE_AGENT',
  ux_design:       'UX_AGENT',
  sprint_planning: 'SPRINT_AGENT',
  dev_agent:       'DEV_AGENT',
  code_review:     'REVIEW_AGENT',
  qa_agent:        'QA_AGENT',
  deployment:      'DEVOPS_AGENT',
};

/** Session names that are not phase agents but have their own label. */
const NON_PHASE_SESSION_LABELS: Readonly<Record<string, SupportMeterLabel>> = {
  change_order: 'CHANGE_ORDER',
  help:         'HELP',
};

export function registryKeyToPhaseAgentType(name: string | null | undefined): PhaseAgentType | null {
  if (!name) return null;
  return Object.prototype.hasOwnProperty.call(REGISTRY_KEY_TO_PHASE_AGENT, name)
    ? REGISTRY_KEY_TO_PHASE_AGENT[name]
    : null;
}

/** Label for a session's agent_name. Unknown, null or empty → OTHER. */
export function labelForAgentName(name: string | null | undefined): MeterLabel {
  const phaseAgent = registryKeyToPhaseAgentType(name);
  if (phaseAgent) return phaseAgent;
  if (name && Object.prototype.hasOwnProperty.call(NON_PHASE_SESSION_LABELS, name)) {
    return NON_PHASE_SESSION_LABELS[name];
  }
  return 'OTHER';
}

/** SDLC phase of a label (1, 2, 2.5, …, 7), derived from PHASE_AGENTS; null for support labels. */
export function phaseForLabel(label: MeterLabel): number | null {
  const meta = PHASE_AGENTS.find((a) => a.agentType === label);
  return meta ? meta.phase : null;
}
